import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, ExternalLink, Gamepad2, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Community {
  id: string;
  name: string;
  slug: string;
  description: string;
  cover_image_url?: string | null;
  member_count: number;
  game?: { id: string; name: string } | null;
}

interface MentorCommunitiesSectionProps {
  communities: Community[];
}

export const MentorCommunitiesSection: React.FC<MentorCommunitiesSectionProps> = ({ 
  communities 
}) => {
  const navigate = useNavigate();

  if (!communities || communities.length === 0) {
    return null;
  }

  const handleCommunityClick = (community: Community) => {
    navigate(`/comunidades/${community.slug}`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit", 
      year: "numeric"
    });
  };

  return (
    <Card className="bg-[#131922] border border-[#222A35] mb-8">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
          <Users className="h-6 w-6 text-[#00B6F0]" />
          Comunidades do Mentor
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {communities.map((community) => (
            <Card 
              key={community.id}
              className="bg-[#1A2233] border-[#2A3441] text-white hover:border-[#00B6F0] transition-all duration-300 overflow-hidden group cursor-pointer"
              onClick={() => handleCommunityClick(community)}
            >
              {/* Header com imagem de capa */}
              <div className="relative h-40 overflow-hidden">
                {community.cover_image_url ? (
                  <img 
                    src={community.cover_image_url} 
                    alt={community.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      console.log("❌ Erro ao carregar imagem:", community.cover_image_url);
                      // Fallback para gradiente se a imagem falhar
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-[#2A3441] to-[#1A2233] flex items-center justify-center"><div class="w-16 h-16 text-[#00B6F0] opacity-50"><svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M15 14l-5-5 5 5z"/></svg></div></div>';
                      }
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#2A3441] to-[#1A2233] flex items-center justify-center">
                    <Gamepad2 className="w-16 h-16 text-[#00B6F0] opacity-50" />
                  </div>
                )}
                
                {/* Gradiente overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2233] via-transparent to-transparent" />
                
                {/* Badge do jogo no canto superior esquerdo */}
                {community.game && (
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-[#00B6F0]/90 text-black border-[#00A3D9]/50 backdrop-blur-sm font-medium">
                      {community.game.name}
                    </Badge>
                  </div>
                )}
                
                {/* Ícone de link externo no canto superior direito */}
                <div className="absolute top-3 right-3">
                  <div className="bg-black/50 backdrop-blur-sm rounded-full p-2">
                    <ExternalLink className="h-4 w-4 text-white group-hover:text-[#00B6F0] transition-colors" />
                  </div>
                </div>
              </div>

              <CardContent className="p-4 space-y-3">
                {/* Título da comunidade */}
                <div>
                  <h3 className="text-lg font-bold text-white group-hover:text-[#00B6F0] transition-colors line-clamp-1">
                    {community.name}
                  </h3>
                </div>

                {/* Descrição */}
                <div className="min-h-[2.5rem]">
                  <p className="text-gray-400 text-sm line-clamp-2">
                    {community.description}
                  </p>
                </div>

                {/* Estatísticas na parte inferior */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">{community.member_count} {community.member_count === 1 ? 'membro' : 'membros'}</span>
                  </div>
                  
                  <div className="text-xs text-[#00B6F0] group-hover:text-white transition-colors">
                    Ver comunidade →
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}; 